import { configureEcho } from '@laravel/echo-react'

let echoConfigured = false

// Initialize Echo immediately when module is loaded
// This ensures Echo is configured before any component uses useEcho hook
const initializeEchoSync = () => {
  // Check if Reverb is configured
  const appKey = import.meta.env.VITE_REVERB_APP_KEY
  if (!appKey) {
    return false // No app key, Echo will not be available
  }



  const is_local = import.meta.env.VITE_APP_ENV == "L"
  const host = is_local ? import.meta.env.VITE_REVERB_HOST_LOCAL : import.meta.env.VITE_REVERB_HOST
  const port = is_local ? import.meta.env.VITE_REVERB_PORT_LOCAL : import.meta.env.VITE_REVERB_PORT
  const forceTLS = is_local ? import.meta.env.VITE_REVERB_FORCE_TLS_LOCAL == "true" : import.meta.env.VITE_REVERB_FORCE_TLS == "true"
  const transports = is_local ? ['ws'] : ['ws', "wss"]
  try {
    // Configure Echo using @laravel/echo-react with Reverb broadcaster
    configureEcho({
      broadcaster: 'reverb',
      key: appKey,
      wsHost: host,
      wsPort: port,
      wssPort: port,
      forceTLS: forceTLS,
      encrypted: forceTLS,
      disableStats: true,
      enabledTransports: transports,
      authEndpoint: `${import.meta.env.VITE_API_URL}/broadcasting/auth`,
      auth: {
        headers: (() => {
          // Get token at configuration time
          const token = typeof window !== 'undefined' ? localStorage.getItem('access_token') : null
          const headers = {
            Accept: 'application/json',
          }
          if (token) {
            headers.Authorization = `Bearer ${token}`
          } else if (typeof window !== 'undefined' && import.meta.env.DEV) {
            console.warn('⚠️ No access token found in localStorage for broadcasting auth')
          }
          return headers
        })(),
      },
    })

    echoConfigured = true
    if (typeof window !== 'undefined' && import.meta.env.DEV) {
    }
    return true
  } catch (error) {
    if (typeof window !== 'undefined') {
      console.error('❌ Failed to configure Echo:', error)
    }
    echoConfigured = false
    return false
  }
}

// Initialize on module load
if (typeof window !== 'undefined') {
  initializeEchoSync()

  // Listen for user login events to reconfigure Echo with token
  window.addEventListener('user-logged-in', () => {
    echoConfigured = false
    initializeEchoSync()
  })

  // Also listen for token updates in localStorage
  const originalSetItem = localStorage.setItem
  localStorage.setItem = function (key, value) {
    originalSetItem.apply(this, arguments)
    if (key === 'access_token' && echoConfigured) {
      echoConfigured = false
      initializeEchoSync()
    }
  }
}

/**
 * Configure Laravel Echo using @laravel/echo-react
 * This is now called automatically on module load, but can be called again
 * to update configuration (e.g., when token changes)
 */
export const initializeEcho = () => {
  // Return if already configured
  if (echoConfigured) {
    return true
  }

  // Use the sync initialization
  return initializeEchoSync()
}

/**
 * Update Echo authentication token
 * Note: With @laravel/echo-react, token updates should be handled
 * by reconfiguring Echo or updating the auth header in localStorage
 * @param {string} token - New access token
 */
export const updateEchoAuth = (token) => {
  // Store token in localStorage for auth headers
  localStorage.setItem('access_token', token)

  // Reconfigure Echo with new token
  echoConfigured = false
  initializeEcho()
}

/**
 * Check if Echo is configured
 * @returns {boolean}
 */
export const isEchoConfigured = () => {
  return echoConfigured
}

// Export for backward compatibility (if needed)
// Note: With @laravel/echo-react, prefer using useEcho hook instead
export const getEcho = () => {
  if (!echoConfigured) {
    const initialized = initializeEcho()
    if (!initialized) {
      const appKey = import.meta.env.VITE_REVERB_APP_KEY
      if (appKey) {
        console.warn('⚠️ Echo configuration failed despite Reverb configuration. Check:')
        console.warn('  - Is the Reverb server running? (php artisan reverb:start)')
        console.warn('  - Are REVERB_HOST, REVERB_PORT, and REVERB_SCHEME correct?')
        console.warn('  - Is the broadcasting/auth endpoint accessible?')
      }
      return null
    }
  }

  // Try to get Echo instance from window if available
  // @laravel/echo-react may store it globally
  if (window.Echo) {
    return window.Echo
  }

  // Return a minimal proxy object for backward compatibility
  // Note: This won't work fully with @laravel/echo-react approach
  // Components should migrate to use useEcho hook instead
  return {
    connector: {
      // Fallback connector structure for backward compatibility
      connection: {
        state: 'disconnected',
        bind: () => { },
        unbind: () => { },
        connect: () => { },
      },
      // Pusher-compatible structure (in case @laravel/echo-react uses it internally)
      pusher: {
        connection: {
          state: 'disconnected',
          bind: () => { },
          unbind: () => { },
          connect: () => { },
        },
        config: {
          auth: {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('access_token')}`,
            },
          },
        },
      },
    },
    private: () => ({
      listen: () => { },
      stopListening: () => { },
    }),
    leave: () => { },
    disconnect: () => { },
  }
}

/**
 * Disconnect Echo instance
 * Note: With @laravel/echo-react, connections are managed automatically
 * This is provided for backward compatibility
 */
export const disconnectEcho = () => {
  try {
    // Try to disconnect if Echo instance is available
    if (window.Echo && typeof window.Echo.disconnect === 'function') {
      window.Echo.disconnect()
    }
    echoConfigured = false
  } catch (error) {
    console.warn('⚠️ Error disconnecting Echo:', error)
    echoConfigured = false
  }
}

/**
 * Get the current socket_id from the Echo/Reverb connection
 * @returns {string|null} The socket_id if connected, null otherwise
 */
export const getSocketId = () => {
  try {
    const echo = getEcho()
    if (!echo) {
      return null
    }

    // Check if Echo instance has connector (works for both Pusher and Reverb)
    if (echo.connector) {
      // Try to access connection through connector (works for Reverb and Pusher-compatible connectors)
      const connector = echo.connector

      // For Reverb, the connector structure may differ, but we try common patterns
      if (connector.socketId) {

        return connector.socketId
      }

      // Fallback: try pusher-compatible structure (in case @laravel/echo-react uses it internally)
      if (connector.pusher && connector.pusher.connection) {
        const connection = connector.pusher.connection
        if (connection.state === 'connected' && connection.socket_id) {
          return connection.socket_id
        }
      }

      // Try direct connection access
      if (connector.connection && connector.connection.socket_id) {
        return connector.connection.socket_id
      }
    }

    return null
  } catch (error) {
    if (typeof window !== 'undefined' && import.meta.env.DEV) {
      console.warn('⚠️ Error getting socket_id:', error)
    }
    return null
  }
}

export default { initializeEcho, updateEchoAuth, isEchoConfigured, getEcho, disconnectEcho, getSocketId }

